import { createContext, useContext, useState, ReactNode } from "react";
import { useActiveOrg } from "@/contexts/ActiveOrgContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type ViewMode = "cabinet" | "entreprise";

interface ViewModeContextValue {
  viewMode: ViewMode;
  setViewMode: ((mode: ViewMode) => void) | null;
  canSwitch: boolean;
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null);

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const { activeOrg } = useActiveOrg();
  const { user } = useAuth();
  const [overrideMode, setOverrideMode] = useState<ViewMode | null>(null);

  const { data: userRoles = [] } = useQuery({
    queryKey: ["myRoles", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user!.id);
      return (data ?? []).map(r => r.role);
    },
    enabled: !!user,
  });

  const isSuperAdmin = userRoles.includes("superadmin");
  const orgType = activeOrg?.organization?.type as ViewMode | undefined;

  const viewMode: ViewMode = overrideMode ?? (orgType === "cabinet" ? "cabinet" : "entreprise");
  const canSwitch = isSuperAdmin;

  return (
    <ViewModeContext.Provider value={{
      viewMode,
      setViewMode: canSwitch ? setOverrideMode : null,
      canSwitch,
    }}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode(): ViewModeContextValue {
  const ctx = useContext(ViewModeContext);
  if (!ctx) throw new Error("useViewMode must be used inside ViewModeProvider");
  return ctx;
}
